import React from 'react'
import styled from 'styled-components'
import Paper from '@mui/material/Paper'
import Typography from '@/v2/components/common/Typography'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useTasks } from '@/v2/hooks/useTasks'
import { useUpdateTask } from '@/v2/hooks/useTasks'
import { useClaims } from '@/v2/hooks/insurance/useClaims'
import { format } from 'date-fns'
import { Button } from '@mui/material'
import useModals from '@/v2/hooks/useModals'
import { useNavigate } from 'react-router-dom'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: ${props => (props.$completed ? '#fafafa' : 'white')};
  color: ${props => (props.$completed ? '#999' : '#333')};

  &:hover {
    background: ${props => (props.$completed ? '#f5f5f5' : '#f9f9f9')};
  }
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid #ccc;
  cursor: pointer;
  appearance: none;
  background: white;
  outline: none;
  position: relative;
  flex-shrink: 0;

  &:checked {
    background-color: #4caf50;
    border-color: #4caf50;

    &::after {
      content: '✓';
      color: white;
      font-size: 11px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`

const TaskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TaskTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #2196f3;
  }
`

const TaskMeta = styled.div`
  font-size: 12px;
  color: #666;
`

const EmptyState = styled(Typography)`
  background: #e4e4e4;
  text-align: center;
  padding: 20px 0px;
  opacity: 0.6;
`

const TaskRow = ({ task, patient }) => {
  const { goTo } = useModals()
  const { updateTask } = useUpdateTask(task.id)
  const navigate = useNavigate()
  const { claims = [] } = useClaims(patient?.id)

  const handleTaskCheck = async isChecked => {
    try {
      await updateTask({
        completed: isChecked,
      })
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const handleTaskClick = () => {
    goTo('/v2/task/new', { task })
  }

  const handleLinkableClick = () => {
    const patientId = task.linkable?.patientId || task.patient?.id

    if (!patientId) return

    if (task.linkable?.type === 'Insurance::Claim') {
      const claimId = task.linkable.id
      const claim = claims.find(c => c.id === claimId)

      if (claim) {
        navigate(`/v2/patient/${patientId}/insurance`, {
          state: {
            openTab: 'claims',
            openClaimId: claimId,
            claim: claim,
            patient: task.patient,
            scrollToHistory: true,
          },
        })
      } else {
        navigate(`/v2/patient/${patientId}/insurance`, {
          state: {
            openTab: 'claims',
            openClaimId: claimId,
            scrollToHistory: true,
          },
        })
      }
    } else {
      const url = `/v2/patient/${patientId}/profile#patient-insurance`
      window.location.href = url
    }
  }

  return (
    <TaskItem $completed={task.completed}>
      <Checkbox
        checked={task.completed}
        onChange={e => handleTaskCheck(e.target.checked)}
      />
      <TaskContent>
        <TaskTitle onClick={handleTaskClick}>{task.title}</TaskTitle>
        <TaskMeta>
          {task.createdAt &&
            `Created ${format(new Date(task.createdAt), 'MMM dd, yyyy')}`}
          {task.dueDate &&
            ` • Due ${format(new Date(task.dueDate), 'MMM dd, yyyy')}`}
          {task.assignedBy &&
            ` • Assigned by ${task.assignedBy.fullName ||
              task.assignedBy.email}`}
        </TaskMeta>
      </TaskContent>
      {task.linkable && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleLinkableClick}
          style={{ minWidth: 'auto', flexShrink: 0 }}
        >
          {task.linkable.type === 'InsuranceForm'
            ? 'Review Insurance'
            : task.linkable.type === 'Insurance::Claim'
            ? 'Review Claim'
            : 'View'}
        </Button>
      )}
    </TaskItem>
  )
}

const Tasks = () => {
  const { patient } = usePatientContext()
  const { tasks = [], isLoading } = useTasks()

  const patientTasks = tasks
    .filter(task => task.patient?.id === patient?.id)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

  return (
    <Paper sx={{ padding: '30px', marginBottom: '20px' }}>
      <Header>
        <Typography variant="h3" sx={{ mb: '28px' }}>
          Tasks ({patientTasks.length})
        </Typography>
      </Header>
      {isLoading ? (
        <EmptyState>Loading tasks...</EmptyState>
      ) : patientTasks.length === 0 ? (
        <EmptyState>No tasks yet</EmptyState>
      ) : (
        <TaskList>
          {patientTasks.map(task => (
            <TaskRow key={task.id} task={task} patient={patient} />
          ))}
        </TaskList>
      )}
    </Paper>
  )
}

export default Tasks
